import lodash from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import withDraggable from '~app/core/withDraggable';
import EditableLabel from './EditableLabel';
import { MAX_LABEL_LENGTH } from '../consts';

import { ReactComponent as Regulator } from '~app/resources/images/regulator.svg';
import { ReactComponent as Stock } from '~app/resources/images/stock.svg';
import { ReactComponent as Variable } from '~app/resources/images/variable.svg';

const icons = {
  flow: Regulator,
  variable: Variable,
  stock: Stock,
};

// Valid types: unassigned, variable, stock, flow object
// Renders the 'body' of the node, but not the arrows
// if onLabelChange is defined, the node is in edit mode: text is an input and is focusable
function CanvasNode({
  label,
  type,
  selected,
  onLabelChange,
  onAnchorDataChange,
  hasError,
  hasSourceError,
  hasSinkError,
  pendingRecheck,
  labelFocus,
}) {
  const Icon = icons[type];
  const topRef = useRef();
  const iconRef = useRef();
  const svgRef = useRef();
  const labelRef = useRef();
  const [anchorData, setAnchorData] = useState({
    center: { x: 0, y: 0 }, // point relative to the origin of this element
    flowPoints: [], // relative to the center
    lines: [], // relative to the center
  });
  const [svgData, setSvgData] = useState({
    center: { x: 0, y: 0 }, // point relative to the origin of this element
    flowPoints: [], // relative to the center
    lines: [], // relative to the center
  });
  const editMode = useSelector(state => state.document.present?.editMode);
  const notInSolutionMode = editMode !== 'solution';

  const labelOnClick = useCallback(
    e => {
      if (iconRef.current) iconRef.current.focus();
    },
    [iconRef],
  );

  useEffect(() => {
    if (onAnchorDataChange) {
      const elem = topRef.current.parentElement;
      onAnchorDataChange(elem, anchorData);
    }
  }, [onAnchorDataChange, anchorData, topRef]);

  useEffect(() => {
    if (svgRef.current) {
      // TODO: there's a potential optimization that can be done here.
      // Since this data is the same for each instance of a given icon,
      // the results of the following getElementsByClassName calls should
      // be cached.
      const data = {
        lines: [],
        flowPoints: [],
      };
      const center = svgRef.current.getElementsByClassName('node_center')[0];
      if (center) {
        const x = center.cx.animVal.value;
        const y = center.cy.animVal.value;
        data.center = { x, y };
      }
      for (const e of svgRef.current.getElementsByClassName(
        'node_anchor_line',
      )) {
        const x1 = e.x1.animVal.value;
        const y1 = e.y1.animVal.value;
        const x2 = e.x2.animVal.value;
        const y2 = e.y2.animVal.value;
        data.lines.push({ x1, y1, x2, y2 });
      }
      for (const e of svgRef.current.getElementsByClassName(
        'node_flow_point',
      )) {
        const x = e.cx.animVal.value;
        const y = e.cy.animVal.value;
        data.flowPoints.push({ x, y });
      }
      setSvgData(data);
    } else {
      setSvgData(null);
    }
  }, [svgRef, type]);

  useEffect(() => {
    const elem = labelRef.current;
    const x1 = elem.offsetLeft + topRef.current.offsetLeft - 2;
    const x2 = x1 + elem.clientWidth + elem.clientLeft * 2 + 4;
    const y1 = elem.offsetTop + topRef.current.offsetTop - 2;
    const y2 = y1 + elem.clientHeight + elem.clientTop * 2 + 4;
    const textLines = [
      { x1, y1, x2: x1, y2 },
      { x1, y1: y2, x2, y2 },
      { x1: x2, y1, x2, y2 },
      { x1, y1, x2, y2: y1 },
    ];

    const data = {
      lines: [],
      flowPoints: [],
      center: {
        x: x1 + (x2 - x1) / 2,
        y: y1 + (y2 - y1) / 2,
      },
    };

    if (svgData && iconRef.current) {
      const offsetX = iconRef.current.offsetLeft + topRef.current.offsetLeft;
      const offsetY = iconRef.current.offsetTop + topRef.current.offsetTop;
      if (svgData.center) {
        const x = svgData.center.x + offsetX;
        const y = svgData.center.y + offsetY;
        data.center = { x, y };
      }
      for (const e of svgData.lines) {
        const x1 = e.x1 + offsetX - data.center.x;
        const y1 = e.y1 + offsetY - data.center.y;
        const x2 = e.x2 + offsetX - data.center.x;
        const y2 = e.y2 + offsetY - data.center.y;
        data.lines.push({ x1, y1, x2, y2 });
      }
      for (const e of svgData.flowPoints) {
        const x = e.x + offsetX - data.center.x;
        const y = e.y + offsetY - data.center.y;
        data.flowPoints.push({ x, y });
      }
    }

    // translate these so they are relative to data.center
    for (const line of textLines) {
      line.x1 -= data.center.x;
      line.y1 -= data.center.y;
      line.x2 -= data.center.x;
      line.y2 -= data.center.y;
      data.lines.push(line);
    }

    setAnchorData(data);
  }, [svgData, labelRef, iconRef, topRef, type, label]);

  return (
    <div ref={topRef} className={`canvas__node canvas__${type}_node`}>
      {hasError && (
        <div
          className={`canvas__node__errormark ${
            pendingRecheck?.node ? 'canvas__node__errormark-pending' : ''
          }`}
        ></div>
      )}
      {hasSourceError && (
        <div
          className={`canvas__node__errormark canvas__node__errormark-source ${
            pendingRecheck?.flowSource ? 'canvas__node__errormark-pending' : ''
          }`}
        ></div>
      )}

      {hasSinkError && (
        <div
          className={`canvas__node__errormark canvas__node__errormark-sink ${
            pendingRecheck?.flowSink ? 'canvas__node__errormark-pending' : ''
          }`}
        ></div>
      )}

      {Icon && (
        <div
          className={`canvas__node_icon handle ${selected ? 'selected' : ''}`}
          ref={iconRef}
          tabIndex={0}
          aria-dropeffect={selected ? 'move' : 'none'}
          aria-grabbed={selected}
          aria-current={selected}
          role="button"
          aria-label={`draggable object ${label} ${
            type !== 'unassigned' ? `type ${type}` : ''
          } ${selected ? ' current' : ''}`}
        >
          <Icon ref={svgRef} />
        </div>
      )}
      {/* // Note aria-label overrides aria-current and content of the div */}
      <div
        ref={labelRef}
        aria-dropeffect={selected ? 'move' : 'none'}
        aria-grabbed={selected}
        aria-current={selected}
        role="button"
        aria-label={`draggable object ${label} ${
          type !== 'unassigned' ? `type ${type}` : ''
        } ${selected ? ' current' : ''}`}
        className={`canvas__label handle ${selected ? 'selected' : ''}`}
        onClick={onLabelChange && selected ? undefined : labelOnClick}
        tabIndex={(Icon || onLabelChange) && notInSolutionMode ? undefined : 0}
      >
        <EditableLabel
          className="canvas__label__text"
          isEditable={!!onLabelChange && selected}
          value={label}
          onFinalChange={onLabelChange}
          maxLength={MAX_LABEL_LENGTH}
          labelFocus={labelFocus}
        />
      </div>
    </div>
  );
}

export default React.memo(
  withDraggable(CanvasNode, {
    draggableProps: {
      handle: '.handle',
      defaultClassName: 'canvas-draggable',
      cancel: '.canvas-object',
    },
  }),
  lodash.isEqual,
);
